import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePickerField } from "@/components/DatePickerField";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Lock, RotateCcw } from "lucide-react";

// ID-063: reorder an approved dealer-exclusive custom design (same specs, colors and composition)
export const ReorderDesign = ({ req }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(String(req.quantity_sqft || ""));
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dealer/custom-studio/${req.id}/reorder`, {
        quantity_sqft: Number(qty) || null, desired_delivery_date: date || null, notes: notes || null,
      });
      toast.success(`Reorder ${data.request_number} sent to Zelligio`);
      setOpen(false);
      navigate(`/dealer/custom-studio/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-sand/50 p-4" data-testid="custom-exclusive-panel">
      <p className="flex items-center gap-2 text-sm text-charcoal" data-testid="custom-exclusive-note">
        <Lock className="h-4 w-4 text-terracotta" /> Exclusive to your account — never shown to other dealers or added to the catalog.
      </p>
      {req.reorderable && (
        <Button onClick={() => setOpen(true)} data-testid="custom-reorder-button" className="gap-2 bg-terracotta text-white hover:bg-terracotta-hover">
          <RotateCcw className="h-4 w-4" /> Reorder this design
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="custom-reorder-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Reorder {req.design_name}</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Same approved specifications, colors and composition as {req.request_number}. Zelligio confirms production and pricing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label htmlFor="ro-qty">Quantity (sq ft)</Label>
              <Input id="ro-qty" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="custom-reorder-qty" className="mt-1 h-11" /></div>
            <div className="space-y-1"><Label>Needed by (optional)</Label>
              <DatePickerField value={date} onChange={setDate} min="today" testId="custom-reorder-date" /></div>
            <div><Label htmlFor="ro-notes">Notes (optional)</Label>
              <Textarea id="ro-notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="custom-reorder-notes" className="mt-1" /></div>
            <Button onClick={submit} disabled={busy || !(Number(qty) > 0)} data-testid="custom-reorder-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reorder"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
